import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { ReactElement } from "react";
import { createPortal } from "react-dom";
import type { Benachrichtigung } from "@kundeninfo/api-client";
import { Icon, useEscapeEbene } from "@kundeninfo/ui";
import { Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { formatZeitpunkt, relativeZeit } from "../../format.js";
import type { BenachrichtigungenClient } from "./benachrichtigungenClient.js";
import "./Glocke.css";

/**
 * DIE GLOCKE (Spec Tickets, Nachtrag, F105, F106): Werkzeugknopf der Kopfleiste (Übergang P12-1)
 * mit Zähler der ungelesenen Benachrichtigungen, darunter die Liste (Übergang T-9 — me-design hat
 * weder Glocke mit Zähler noch eine Benachrichtigungsliste).
 *
 * Der Zähler lädt beim Start, alle 60 s SOLANGE DER TAB SICHTBAR IST (verdeckt ruht der Takt,
 * beim Wiederauftauchen wird sofort nachgefragt), bei Rückkehr des Fokus und sofort, wenn der
 * Service Worker einen Push meldet. Kein WebSocket. Ungelesene oben (Hausregel „Schlechtestes
 * oben"); ein Klick öffnet das Ticket und markiert die Zeile gelesen. Escape läuft über den
 * gemeinsamen Escape-Stapel und gibt den Fokus an die Glocke zurück.
 *
 * Der einmalige Hinweis (Entscheid 182): Kann das Gerät Mitteilungen und wurde noch nie
 * gefragt (`Notification.permission === "default"`), erscheint beim ersten Zeigen der Glocke
 * EIN schwebender Hinweis, der auf Einstellungen › Benachrichtigungen führt. Das Merkzeichen
 * `ki-push-hinweis` wird schon beim Zeigen gesetzt — „einmal je Gerät" heisst einmal, nicht
 * „bis er weggeklickt wurde".
 */

const HINWEIS_SCHLUESSEL = "ki-push-hinweis";
const TAKT_MS = 60_000;

function hinweisFaellig(): boolean {
  try {
    return typeof Notification !== "undefined" && Notification.permission === "default"
      && localStorage.getItem(HINWEIS_SCHLUESSEL) !== "gesehen";
  } catch { return false; }
}

function hinweisGesehen(): void {
  try { localStorage.setItem(HINWEIS_SCHLUESSEL, "gesehen"); } catch { /* privater Modus */ }
}

const SYMBOL: Record<Benachrichtigung["kind"], string> = {
  CHANGE: "pen", COMMENT: "comment", RESUBMIT_DUE: "clock",
};

export type GlockeProps = {
  client: BenachrichtigungenClient;
  onOeffnen: (ticketId: string) => void;
  onEinstellungen: () => void;
};

export function Glocke(p: GlockeProps): ReactElement {
  const [zahl, setZahl] = useState(0);
  const [offen, setOffen] = useState(false);
  const [liste, setListe] = useState<Benachrichtigung[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState(hinweisFaellig);
  const huelle = useRef<HTMLDivElement | null>(null);
  const knopf = useRef<HTMLButtonElement | null>(null);
  const panel = useRef<HTMLElement | null>(null);
  const panelId = useId();
  const offenRef = useRef(offen);
  offenRef.current = offen;

  const zaehlen = useCallback(async () => {
    try { setZahl((await p.client.countNotifications()).ungelesen); } catch { /* nächster Takt */ }
  }, [p.client]);
  const laden = useCallback(async () => {
    try {
      const r = await p.client.listNotifications({ limit: 30 });
      setListe(r.items); setZahl(r.ungelesen); setFehler(null);
    } catch { setFehler("Die Benachrichtigungen konnten nicht geladen werden."); }
  }, [p.client]);

  useEffect(() => {
    const auffrischen = () => { void zaehlen(); if (offenRef.current) void laden(); };
    let takt: ReturnType<typeof setInterval> | null = null;
    const sichtbar = () => document.visibilityState !== "hidden";
    const starte = () => { if (takt === null) takt = setInterval(() => { void zaehlen(); }, TAKT_MS); };
    const halte = () => { if (takt !== null) { clearInterval(takt); takt = null; } };
    const sichtWechsel = () => { if (sichtbar()) { auffrischen(); starte(); } else halte(); };

    void zaehlen();
    if (sichtbar()) starte();
    document.addEventListener("visibilitychange", sichtWechsel);
    window.addEventListener("focus", auffrischen);
    const sw = typeof navigator !== "undefined"
      ? (navigator as { serviceWorker?: EventTarget }).serviceWorker : undefined;
    const nachricht = (e: Event) => {
      if (((e as MessageEvent).data as { art?: unknown } | null)?.art === "ki:benachrichtigung") auffrischen();
    };
    sw?.addEventListener("message", nachricht);
    return () => {
      halte();
      document.removeEventListener("visibilitychange", sichtWechsel);
      window.removeEventListener("focus", auffrischen);
      sw?.removeEventListener("message", nachricht);
    };
  }, [zaehlen, laden]);

  // Einmal je Gerät: das Merkzeichen gilt, sobald der Hinweis zu sehen war.
  useEffect(() => { if (hinweis) hinweisGesehen(); }, [hinweis]);

  useEscapeEbene(offen, () => { setOffen(false); knopf.current?.focus(); });

  useEffect(() => {
    if (!offen) return undefined;
    panel.current?.focus();
    const ausserhalb = (e: PointerEvent) => { if (!huelle.current?.contains(e.target as Node)) setOffen(false); };
    document.addEventListener("pointerdown", ausserhalb);
    return () => { document.removeEventListener("pointerdown", ausserhalb); };
  }, [offen]);

  const umschalten = () => {
    const neu = !offen; setOffen(neu);
    // Wer die Glocke öffnet, hat sie gefunden: Der einmalige Hinweis ist erledigt und liegt nicht
    // über der Liste.
    if (neu) { setHinweis(false); void laden(); }
  };

  function oeffne(b: Benachrichtigung): void {
    setOffen(false);
    if (b.readAt === null) {
      const jetzt = new Date().toISOString();
      setZahl((z) => Math.max(0, z - 1));
      setListe((l) => (l ?? []).map((x) => (x.id === b.id ? { ...x, readAt: jetzt } : x)));
      // Scheitert das Markieren, holt der nächste Takt den richtigen Zähler.
      void p.client.markNotificationsRead({ ids: [b.id] }).catch(() => undefined);
    }
    p.onOeffnen(b.ticketId);
  }

  async function alleGelesen(): Promise<void> {
    try {
      await p.client.markNotificationsRead({ alle: true });
      const jetzt = new Date().toISOString();
      setZahl(0); setFehler(null);
      setListe((l) => (l ?? []).map((b) => ({ ...b, readAt: b.readAt ?? jetzt })));
    } catch { setFehler("Die Benachrichtigungen konnten nicht als gelesen markiert werden."); }
  }

  function zuDenEinstellungen(): void {
    hinweisGesehen();
    setHinweis(false); setOffen(false); p.onEinstellungen();
  }

  const sortiert = [...(liste ?? [])].sort((a, b) =>
    Number(a.readAt !== null) - Number(b.readAt !== null) || b.occurredAt.localeCompare(a.occurredAt));
  const ungelesenInListe = sortiert.some((b) => b.readAt === null);

  return (
    <div className="gl kz__werkzeug-huelle" ref={huelle}>
      {/* Übergang P12-1 (docs/ME-DESIGN.md): Werkzeugknopf der Kopfleiste in der Umgebungsfarbe. */}
      <button type="button" ref={knopf} className="kz__werkzeug kz__tapflaeche gl__knopf"
        aria-expanded={offen} aria-controls={offen ? panelId : undefined}
        aria-label={zahl === 0 ? "Benachrichtigungen" : `Benachrichtigungen, ${zahl} ungelesen`}
        title="Benachrichtigungen" onClick={umschalten}>
        <Icon name="bell" size={22} />
        {zahl > 0 && <span className="gl__zahl" aria-hidden="true">{zahl > 99 ? "99+" : zahl}</span>}
      </button>
      {offen && (
        // Übergang T-9 (docs/ME-DESIGN.md): Benachrichtigungsliste unter der Glocke. Ein
        // Aufklapper (Glocke mit aria-expanded) über einem benannten Bereich — KEIN Dialog:
        // nicht modal, keine Fokusfalle, wie das Zahnrad-Menü (striktesModal.test.tsx).
        <section id={panelId} className="gl__panel kz__menu" aria-label="Benachrichtigungen"
          tabIndex={-1} ref={panel}>
          <div className="gl__kopf">
            <h2 id={`${panelId}-titel`} className="gl__titel">Benachrichtigungen</h2>
            <KiKnopf art="leise" deaktiviert={zahl === 0 && !ungelesenInListe}
              onClick={() => { void alleGelesen(); }}>Alle als gelesen markieren</KiKnopf>
          </div>
          {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
          {liste !== null && sortiert.length === 0 && fehler === null
            && <p className="gl__leer">Keine Benachrichtigungen.</p>}
          {sortiert.length > 0 && (
            <ul className="gl__liste" aria-labelledby={`${panelId}-titel`}>
              {sortiert.map((b) => (
                <li key={b.id} className={b.readAt === null ? "gl__zeile gl__zeile--neu" : "gl__zeile"}>
                  <button type="button" className="gl__eintrag" onClick={() => oeffne(b)}>
                    <Icon name={SYMBOL[b.kind]} size={16} className="gl__symbol" />
                    <span className="gl__text">
                      <span className="gl__ticket">{b.ticketNumber} · {b.ticketTitle}</span>
                      <span className="gl__was">
                        {b.eventCount > 1 ? `${b.summary} (+${b.eventCount - 1} weitere)` : b.summary}
                      </span>
                    </span>
                    <time className="gl__zeit" dateTime={b.occurredAt} title={formatZeitpunkt(b.occurredAt)}>
                      {relativeZeit(b.occurredAt)}
                    </time>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {hinweis && createPortal(
        /*
         * Befund 1 (Task 13): Der Sprungknopf steht UNTER dem Text und nicht im Slot `aktionen`.
         * Dort nahm er als `flex: none` der Textspalte die Breite, am Telefon bis auf null — der
         * Hinweis wurde 1505 px hoch, ein Zeichen je Zeile, und verdeckte Glocke, Zahnrad und
         * untere Leiste. Lage und Breite setzt `.gl__hinweis` (Glocke.css): eine feste Kiste
         * unter der Kopfleiste rechts, am Telefon unten mittig über der unteren Leiste.
         */
        <Hinweis art="info" schwebend schliessbar className="gl__hinweis"
          aria-label="Mitteilungen auf diesem Gerät" onGeschlossen={() => setHinweis(false)}>
          <strong className="gl__hinweis-frage">Mitteilungen auf diesem Gerät einschalten?</strong>
          <p className="gl__hinweis-text">Push-Benachrichtigungen kannst Du in den Einstellungen je Gerät einschalten.</p>
          <KiKnopf art="leise" onClick={zuDenEinstellungen}>Zu den Einstellungen</KiKnopf>
        </Hinweis>,
        document.body,
      )}
    </div>
  );
}
